import React, { useEffect } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Toaster } from './components/ui/toaster';
import Landing from './components/Landing';
import Dashboard from './components/Dashboard';
import Compiler from './components/Compiler';
import AIPrompt from './components/AIPrompt';
import VisualEditor from './components/VisualEditor';
import VisualFormEditor from './components/VisualFormEditor';
import ExampleStory from './components/ExampleStory';
import UsageTypeDemo from './components/UsageTypeDemo';
import EmbeddedStoryView from './components/EmbeddedStoryView';
import ThankYouDonation from './components/ThankYouDonation';
import LinkedInSlideCompiler from './components/LinkedInSlideCompiler';
import VisualStoryPlanner from './components/VisualStoryPlanner';
import SampleGallery from './components/SampleGallery';
import TourPage from './components/TourPage';
import PlayerSessionView from './components/PlayerSessionView';
import JourneyWizard from './components/JourneyWizard';
import StoryAnalytics from './components/StoryAnalytics';
import SitemapImporter from './components/SitemapImporter';
import ProFeatures from './components/ProFeatures';
import AgencyFeatures from './components/AgencyFeatures';
import Features from './components/Features';
import Pricing from './components/Pricing';
import Home2 from './components/home2/Home2';
import Landing3 from './components/Landing3';
import ChecklistWizard from './components/ChecklistWizard';
import AdminPanel from './components/AdminPanel';
import FreePreviewWorkspace from './components/FreePreviewWorkspace';
import CustomerAuthCallback from './components/CustomerAuthCallback';
import AdvancedCompilerAccess from './components/AdvancedCompilerAccess';
import CookieNotice from './components/CookieNotice';
import { PrivacyPage, TermsPage } from './components/LegalPages';
import { useAdminAuth } from './hooks/useAdminAuth';

// Demo Hub (isolated module - can be removed by deleting this import and route)
import { DemoHub, DEMO_HUB_ENABLED } from './demos/ecom-lab';

/**
 * Utility to validate and sanitize localStorage data on app startup
 * Checks for and removes duplicate passages that may have accumulated
 */
const sanitizeLocalStorageData = () => {
  const APP_VERSION_KEY = 'tweeporter_app_version';
  const CURRENT_VERSION = '1.0.6';
  
  // Check if this is a new session or version change
  const storedVersion = localStorage.getItem(APP_VERSION_KEY);
  const isNewSession = !sessionStorage.getItem('tweeporter_session_initialized');
  
  // Always mark session as initialized
  sessionStorage.setItem('tweeporter_session_initialized', 'true');
  
  // Helper to check for duplicate passages in Twee code
  const hasDuplicatePassages = (tweeCode) => {
    if (!tweeCode) return false;
    const passageMatches = tweeCode.match(/^:: .+$/gm) || [];
    const passageNames = passageMatches.map(match => match.substring(3).split('[')[0].trim());
    const uniqueNames = new Set(passageNames);
    return uniqueNames.size !== passageNames.length;
  };
  
  // Helper to remove duplicate passages from Twee code
  const deduplicateTwee = (tweeCode) => {
    if (!tweeCode) return tweeCode;
    
    const passages = [];
    const lines = tweeCode.split('\n');
    let currentPassage = null;
    const seenPassageNames = new Set();
    
    for (const line of lines) {
      if (line.trim().startsWith(':: ')) {
        // Save previous passage if valid and not duplicate
        if (currentPassage && currentPassage.content.trim()) {
          if (!seenPassageNames.has(currentPassage.name)) {
            passages.push(currentPassage);
            seenPassageNames.add(currentPassage.name);
          }
        }
        const name = line.trim().substring(3).split('[')[0].trim();
        currentPassage = { name, content: '' };
      } else if (currentPassage) {
        currentPassage.content += line + '\n';
      }
    }
    
    // Don't forget the last passage
    if (currentPassage && currentPassage.content.trim()) {
      if (!seenPassageNames.has(currentPassage.name)) {
        passages.push(currentPassage);
      }
    }
    
    // Rebuild the Twee code
    return passages.map(p => `:: ${p.name}\n${p.content.trim()}`).join('\n\n');
  };
  
  // Check and sanitize visual_editor_twee
  const savedTwee = localStorage.getItem('visual_editor_twee');
  if (savedTwee && hasDuplicatePassages(savedTwee)) {
    console.warn('[TweePorter] Detected duplicate passages in localStorage, cleaning...');
    const cleanedTwee = deduplicateTwee(savedTwee);
    if (cleanedTwee && cleanedTwee.length > 0) {
      localStorage.setItem('visual_editor_twee', cleanedTwee);
    } else {
      localStorage.removeItem('visual_editor_twee');
    }
  }
  
  // On version change, clear potentially stale data
  if (storedVersion !== CURRENT_VERSION) {
    console.log(`[TweePorter] Version changed from ${storedVersion} to ${CURRENT_VERSION}, clearing cache`);
    // Clear potentially corrupted data on version update
    localStorage.removeItem('visual_editor_graph');
    localStorage.removeItem('compiler_twee_code');
    localStorage.removeItem('compiler_action');
    // Keep user data: tweeporter_user, saved stories, etc.
    localStorage.setItem(APP_VERSION_KEY, CURRENT_VERSION);
  }
  
  // On new browser session, clear transient editor state
  if (isNewSession) {
    console.log('[TweePorter] New session detected, clearing transient state');
    // Clear session-transient data that shouldn't persist across browser sessions
    localStorage.removeItem('visual_editor_graph');
    sessionStorage.removeItem('imported_twee');
  }
};

const ProtectedRoute = ({ children }) => {
  const user = localStorage.getItem('tweeporter_user');
  return user ? children : <Navigate to="/login" />;
};

const AdminOnlyRoute = ({ children }) => {
  const { user, loading } = useAdminAuth();
  if (loading) return <div className="min-h-screen bg-slate-50" data-testid="admin-route-loading" />;
  return user ? children : <Navigate to="/free" replace />;
};

const AppContent = () => {
  const [clickCount, setClickCount] = React.useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  if (location.pathname === '/diy' && location.hash?.includes('session_id=')) return <CustomerAuthCallback />;

  const handleHiddenClick = () => {
    const newCount = clickCount + 1;
    setClickCount(newCount);
    
    if (newCount === 3) {
      // Activate bonus and navigate to thank you page
      navigate('/thank-you-donation');
      setClickCount(0);
    }
  };

  return (
    <>
      {/* Hidden Testing Box - Bottom Right Corner */}
      <div
        onClick={handleHiddenClick}
        style={{
          position: 'fixed',
          bottom: '0px',
          right: '0px',
          width: '30px',
          height: '30px',
          backgroundColor: 'white',
          cursor: 'pointer',
          zIndex: 9999,
          opacity: 0.01
        }}
        title={`Testing: Click ${3 - clickCount} more time${3 - clickCount === 1 ? '' : 's'}`}
      />
      
      <Routes>
          <Route path="/" element={<Landing3 />} />
          <Route path="/old-homepage" element={<AdminOnlyRoute><Landing /></AdminOnlyRoute>} />
          <Route path="/compiler" element={<AdminOnlyRoute><Compiler /></AdminOnlyRoute>} />
          <Route path="/free" element={<FreePreviewWorkspace />} />
          <Route path="/diy" element={<FreePreviewWorkspace diy />} />
          <Route path="/advanced-compiler" element={<AdvancedCompilerAccess />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/demo" element={<AdminOnlyRoute><Compiler /></AdminOnlyRoute>} /> {/* Legacy redirect */}
          <Route path="/compiler/usage-type-config" element={<AdminOnlyRoute><UsageTypeDemo /></AdminOnlyRoute>} />
          <Route path="/demo/usage-type-config" element={<AdminOnlyRoute><UsageTypeDemo /></AdminOnlyRoute>} /> {/* Legacy redirect */}
          <Route path="/embed/:storyId" element={<EmbeddedStoryView />} />
          <Route path="/visual-editor" element={<AdminOnlyRoute><VisualEditor /></AdminOnlyRoute>} />
          <Route path="/visual-form-editor" element={<AdminOnlyRoute><VisualFormEditor /></AdminOnlyRoute>} />
          <Route path="/ai-prompt" element={<AdminOnlyRoute><AIPrompt /></AdminOnlyRoute>} />
          <Route path="/examples/:storyId" element={<ExampleStory />} />
          <Route path="/examples" element={<SampleGallery />} />
          <Route path="/thank-you-donation" element={<ThankYouDonation />} />
          <Route path="/linkedin-slide-compiler" element={<AdminOnlyRoute><LinkedInSlideCompiler /></AdminOnlyRoute>} />
          <Route path="/visual-story-planner" element={<AdminOnlyRoute><VisualStoryPlanner /></AdminOnlyRoute>} />
          <Route path="/journey-wizard" element={<AdminOnlyRoute><JourneyWizard /></AdminOnlyRoute>} />
          <Route path="/story-analytics/:storyId" element={<AdminOnlyRoute><StoryAnalytics /></AdminOnlyRoute>} />
          <Route path="/sitemap-to-pathnav" element={<AdminOnlyRoute><SitemapImporter /></AdminOnlyRoute>} />
          <Route path="/sample-gallery" element={<SampleGallery />} />
          <Route path="/pro" element={<AdminOnlyRoute><ProFeatures /></AdminOnlyRoute>} />
          <Route path="/agency" element={<AdminOnlyRoute><AgencyFeatures /></AdminOnlyRoute>} />
          <Route path="/features" element={<Features />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/home2" element={<AdminOnlyRoute><Home2 /></AdminOnlyRoute>} />
          <Route path="/home3" element={<Landing3 />} />
          <Route path="/checklist" element={<AdminOnlyRoute><ChecklistWizard /></AdminOnlyRoute>} />
          <Route path="/admin" element={<AdminPanel />} />
          {/* Hidden Demo Hub - isolated module */}
          {DEMO_HUB_ENABLED && <Route path="/sheret/*" element={<AdminOnlyRoute><DemoHub /></AdminOnlyRoute>} />}
          <Route path="/tour/:tourId/step/:stepNum" element={<TourPage />} />
          <Route path="/session/:sessionId" element={<PlayerSessionView />} />
          <Route path="/login" element={<Navigate to="/free" replace />} />
          <Route path="/signup" element={<Navigate to="/free" replace />} />
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } 
          />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      <Toaster />
      <CookieNotice />
    </>
  );
};

function App() {
  // Run sanitization on app startup
  useEffect(() => {
    sanitizeLocalStorageData();
  }, []);
  
  return (
    <div className="App">
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </div>
  );
}

export default App;
